// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — the deck is a FINITE PROJECTIVE PLANE of prime order n. It has n²+n+1
// "points" (symbols) and n²+n+1 "lines" (cards); each card carries n+1 symbols, and —
// the property the entire game rests on — every two distinct cards meet in EXACTLY one
// symbol. That is why a pair of cards always has one, and only one, right answer.
//
// buildPlane() is a pure, deterministic construction (no rng). The play SEQUENCE (which
// order the cards come out in, extended long enough for the timed/endless modes) is a
// pure function of (order, seed), which is the whole P2P-sync surface: two peers on the
// same frozen seed walk byte-identical cards with nothing on the wire.

import { makeRng } from '@ben-gy/game-engine/rng';

/** Small primes we allow. The simple incidence construction below is exact only for a
 *  PRIME order (not a general prime power), so we restrict to primes and test the invariant. */
const PRIMES = new Set([2, 3, 5, 7, 11, 13]);

/**
 * The cards of a projective plane of prime order `n`. Returns n²+n+1 cards, each an array
 * of n+1 symbol ids in [0, n²+n]. Every two cards share exactly one symbol.
 */
export function buildPlane(n: number): number[][] {
  if (!PRIMES.has(n)) throw new Error(`buildPlane: order ${n} is not a supported prime`);
  const cards: number[][] = [];

  // n+1 cards through the "vertical" point 0.
  for (let i = 0; i <= n; i++) {
    const card = [0];
    for (let j = 0; j < n; j++) card.push(i * n + j + 1);
    cards.push(card);
  }
  // n² cards, one per (slope i, intercept j).
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const card = [i + 1];
      for (let k = 0; k < n; k++) card.push(n + 1 + n * k + ((i * k + j) % n));
      cards.push(card);
    }
  }
  return cards;
}

/** Total number of symbols/cards for a plane of order n. */
export function planeSize(n: number): number {
  return n * n + n + 1;
}

/** A seeded in-place Fisher–Yates over a copy, using the engine PRNG for P2P determinism. */
function shuffled<T>(items: readonly T[], rnd: () => number): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * The ordered card sequence a run walks. `minLen` cards are produced by concatenating
 * seeded shuffles of the plane, de-seaming so no two CONSECUTIVE cards are identical
 * (identical consecutive cards would share every symbol — no unique answer). Deterministic
 * in (order, seed).
 */
export function buildSequence(order: number, seed: number, minLen: number): number[][] {
  const cards = buildPlane(order);
  const rnd = makeRng(seed >>> 0);
  const seq: number[][] = [];
  let guard = 0;
  while (seq.length < minLen && guard++ < 10_000) {
    let block = shuffled(cards, rnd);
    // de-seam: if this block would repeat the previous last card, rotate it once. (Wrap
    // block[0] in its own array — a bare .concat(card) would SPREAD the card's numbers.)
    if (seq.length && block[0] === seq[seq.length - 1]) {
      block = [...block.slice(1), block[0]];
    }
    for (const c of block) seq.push(c);
  }
  return seq.slice(0, Math.max(minLen, cards.length));
}

/**
 * The unique symbol shared by two cards. Independent, tiny, and used by the game itself —
 * the mechanism audit re-derives its OWN copy rather than importing this, so a bug here
 * cannot hide behind a matching bug in the test. Returns -1 if (impossibly) there is none.
 */
export function sharedSymbol(a: readonly number[], b: readonly number[]): number {
  const set = new Set(a);
  for (const s of b) if (set.has(s)) return s;
  return -1;
}
